import React, { ReactElement } from "react";
import {
  Column,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableFooterCell,
  formatValue,
  StyledTable,
  getNestedValue,
} from "./BaseTable";

interface RegularTableProps<T = any> {
  id?: string;
  columns: Column<T>[];
  data: T[];
  includeFooter?: boolean;
  filterNullAnd0Columns?: boolean;
  rotateHeaders?: boolean;
  extraStyle?: string;
  compact?: boolean;
  className?: string;
  lastIsTotal?: boolean;
  "aria-labelledby"?: string;
  showHeader?: boolean;
}

function AddHeaderRows<T extends Record<string, any>>(
  columns: Column<T>[],
  rotateHeaders = false,
  compact = false
): ReactElement {
  return (
    <>
      <tr>
        {columns.length > 0 &&
          columns.map((column, index) => (
            <TableHeaderCell
              key={`th_${index}`}
              rotated={rotateHeaders}
              align={column.align ?? "right"}
              compact={compact}
              extraStyle={`${column.style}`}
            >
              {rotateHeaders ? <div>{column.header}</div> : column.header}
            </TableHeaderCell>
          ))}
      </tr>
    </>
  );
}

function AddDataRows<T extends Record<string, any>>(
  columns: Column<T>[],
  data: T[],
  includeFooter: boolean,
  compact: boolean | undefined,
  lastIsTotal = false
): ReactElement {
  const rows = includeFooter && data.length > 0 ? data.slice(0, -1) : data;

  return (
    <>
      {rows.length > 0 &&
        rows.map((row, rowIndex) => (
          <TableRow
            key={rowIndex}
            index={rowIndex}
            compact={compact}
            className={
              lastIsTotal && rowIndex === rows.length - 1 ? "bold" : ""
            }
          >
            {columns.map((col, colIndex) => {
              const value =
                typeof col.accessor === "function"
                  ? col.accessor(row)
                  : getNestedValue(row, col.accessor as string | keyof T);

              return (
                <TableCell
                  key={colIndex}
                  extraStyle={`${col.style || ""}`} // ${borderStyle}`}
                  className={col.className}
                  highlight={col.highlight}
                  align={col.align ?? "right"}
                  compact={compact}
                >
                  {lastIsTotal &&
                  rowIndex === rows.length - 1 &&
                  colIndex === columns.length - 1
                    ? "+"
                    : formatValue(value, col.dataType)}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
    </>
  );
}

function AddFooterRows<T extends Record<string, any>>(
  columns: Column<T>[],
  data: T[],
  includeFooter: boolean,
  compact = false
): ReactElement {
  if (!includeFooter) return <></>;

  const footer = data[data.length - 1];

  return (
    <>
      {includeFooter && (
        <tr key="last">
          {columns.map((col, colIndex) => {
            const value =
              typeof col.accessor === "function"
                ? col.accessor(footer)
                : getNestedValue(footer, col.accessor as string | keyof T);

            return (
              <TableFooterCell
                key={colIndex}
                extraStyle={`${col.style || ""}`}
                className={col.className}
                align={col.align}
                compact={compact}
              >
                <b>{col.dataType ? formatValue(value, col.dataType) : value}</b>
              </TableFooterCell>
            );
          })}
        </tr>
      )}
    </>
  );
}

export function filterColumns<T extends Record<string, any>>(
  columns: Column<T>[],
  data: T[],
  filterNullAnd0Columns: boolean
): Column<T>[] {
  return columns.filter((column) => {
    // Bepaal of deze kolom gefilterd moet worden op null en 0 waarden
    let shouldFilterNullAnd0: boolean;

    if (column.filterNullAnd0 === null || column.filterNullAnd0 === undefined) {
      // Als column.filterNullAnd0 null/undefined is, gebruik table-niveau setting
      shouldFilterNullAnd0 = filterNullAnd0Columns;
    } else {
      // Anders gebruik de column-specifieke setting
      shouldFilterNullAnd0 = column.filterNullAnd0;
    }

    // Als we niet hoeven te filteren op null/0, toon de kolom altijd
    if (!shouldFilterNullAnd0) {
      return true;
    }

    // Als we wel moeten filteren, check of de kolom non-null en non-zero waarden heeft
    return data.some((row) => {
      const value =
        typeof column.accessor === "function"
          ? column.accessor(row)
          : getNestedValue(row, column.accessor as string | keyof T);
      // Check if value is not null, undefined, 0, or "0"
      return (
        value !== null && value !== undefined && value !== 0 && value !== "0"
      );
    });
  });
}

const RegularTable = <T extends Record<string, any>>({
  id,
  columns,
  data,
  includeFooter = false,
  filterNullAnd0Columns = true,
  rotateHeaders = false,
  extraStyle,
  compact,
  className,
  lastIsTotal = false,
  "aria-labelledby": ariaLabelledby,
  showHeader = true,
}: RegularTableProps<T>): ReactElement => {
  if (!columns || columns.length === 0 || !data || data.length === 0) {
    throw new Error("columns and/or data can't be null, undefined or empty");
  }

  const displayColumns = filterColumns(columns, data, filterNullAnd0Columns);

  return (
    <StyledTable
      id={id}
      data-testid={id}
      extraStyle={extraStyle}
      compact={compact}
      className={`${className} regular-table ${compact ? "compact-table" : ""}`}
      aria-labelledby={ariaLabelledby}
    >
      {showHeader && (
        <TableHeader>
          {AddHeaderRows(displayColumns, rotateHeaders, compact)}
        </TableHeader>
      )}
      <tbody>
        {AddDataRows(displayColumns, data, includeFooter, compact, lastIsTotal)}
        {AddFooterRows(displayColumns, data, includeFooter, compact)}
      </tbody>
    </StyledTable>
  );
};

export default RegularTable;
